import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { supabase } from "../../components/SupabaseClient";
import AuthBrandPanel from "./components/AuthBrandPanel.jsx";
import AuthFormHeader from "./components/AuthFormHeader.jsx";
import AuthShell from "./components/AuthShell.jsx";
import { primaryBtnSx } from "./components/authStyles";

const SCOPE_DESCRIPTIONS = {
  openid: "verify your EFabric identity",
  email: "view your email address and verification status",
  profile: "view your basic profile, such as your name and picture",
  phone: "view your phone number and verification status",
};

const CONSENT_BRAND_CONTENT = {
  title: "Connect securely to EFabric",
  paragraphs: [
    "Review what the requesting application will receive before granting access to your account.",
  ],
  bulletsTitle: "You stay in control",
  bullets: [
    "Your EFabric password is never shared",
    "Access remains limited by your EFabric permissions",
    "You can deny any request you do not recognize",
  ],
};

const detailLabelSx = {
  color: "text.secondary",
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

function OAuthConsentPage({ user }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get("authorization_id")?.trim();
  const userId = user?.id;

  const [authorization, setAuthorization] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    if (!userId) return undefined;

    setAuthorization(null);

    if (!authorizationId) {
      setErrorMsg(
        "This authorization request is missing its identifier. Restart the connection from the requesting application.",
      );
      setLoading(false);
      return undefined;
    }

    let active = true;

    const loadAuthorization = async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const oauth = supabase.auth.oauth;
        if (!oauth?.getAuthorizationDetails) {
          throw new Error("OAuth authorization is unavailable.");
        }

        const { data, error } =
          await oauth.getAuthorizationDetails(authorizationId);

        if (error || !data) {
          throw error || new Error("Authorization request not found.");
        }

        if (!("authorization_id" in data)) {
          if (!data.redirect_url) {
            throw new Error("Missing OAuth redirect URL.");
          }
          window.location.replace(data.redirect_url);
          return;
        }

        if (active) setAuthorization(data);
      } catch (error) {
        console.error("Failed to load OAuth authorization request:", error);
        if (active) {
          setErrorMsg(
            "This authorization request is invalid or has expired. Restart the connection from the requesting application.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAuthorization();
    return () => {
      active = false;
    };
  }, [authorizationId, userId]);

  const requestedScopes = React.useMemo(
    () =>
      (authorization?.scope || "")
        .split(/\s+/)
        .map((scope) => scope.trim())
        .filter(Boolean),
    [authorization?.scope],
  );

  const handleDecision = async (decision) => {
    if (!authorizationId || submitting) return;

    setSubmitting(decision);
    setErrorMsg("");

    try {
      const method =
        decision === "approve"
          ? supabase.auth.oauth?.approveAuthorization
          : supabase.auth.oauth?.denyAuthorization;

      if (!method) throw new Error("OAuth authorization is unavailable.");

      const { data, error } = await method(authorizationId, {
        skipBrowserRedirect: true,
      });

      if (error || !data?.redirect_url) {
        throw error || new Error("Missing OAuth redirect URL.");
      }

      window.location.assign(data.redirect_url);
    } catch (error) {
      console.error(`Failed to ${decision} OAuth request:`, error);
      setErrorMsg(
        `EFabric could not ${decision} this request. Please try again.`,
      );
      setSubmitting("");
    }
  };

  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/signin?redirect=${encodeURIComponent(returnTo)}`}
        replace
      />
    );
  }

  const clientName = authorization?.client?.name || "an application";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: "#EEF1F0",
      }}
    >
      <AuthShell
        maxFormWidth={680}
        brand={<AuthBrandPanel {...CONSENT_BRAND_CONTENT} />}
      >
        <AuthFormHeader
          title={`Authorize ${clientName}`}
          subtitle="An application is requesting access to your EFabric account."
          logoMb={2}
          mb={2.5}
        />

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {errorMsg}
          </Alert>
        )}

        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress size={30} sx={{ color: "#1F4E55" }} />
            <Typography color="text.secondary">
              Loading authorization request…
            </Typography>
          </Stack>
        )}

        {!loading && authorization && (
          <>
            <Box
              sx={{
                border: "1px solid #dbe3e1",
                borderRadius: 2.5,
                p: 2,
                bgcolor: "#f8faf9",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(31,78,85,0.10)",
                    color: "#1F4E55",
                    flexShrink: 0,
                  }}
                >
                  <ShieldOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {clientName}
                  </Typography>
                  {authorization.client.uri && (
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.82rem",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {authorization.client.uri}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1.35}>
                <Box>
                  <Typography sx={detailLabelSx}>Application ID</Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: "0.84rem",
                      color: "#334155",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {authorization.client.id}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={detailLabelSx}>Signed in as</Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <AccountCircleOutlinedIcon
                      sx={{ fontSize: 18, color: "#1F4E55" }}
                    />
                    <Typography sx={{ fontSize: "0.9rem", color: "#0f172a" }}>
                      {authorization.user.email}
                    </Typography>
                  </Stack>
                </Box>

                <Box>
                  <Typography sx={detailLabelSx}>Return address</Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="flex-start"
                    sx={{ mt: 0.5 }}
                  >
                    <OpenInNewRoundedIcon
                      sx={{ fontSize: 17, color: "#1F4E55", mt: "2px" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.84rem",
                        color: "#334155",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {authorization.redirect_uri}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                Requested information
              </Typography>
              {requestedScopes.length > 0 ? (
                <Stack spacing={0.7}>
                  {requestedScopes.map((scope) => (
                    <Stack
                      key={scope}
                      direction="row"
                      spacing={0.85}
                      alignItems="center"
                    >
                      <CheckCircleOutlineRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: "#2A746B",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.88rem", color: "#334155" }}
                      >
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#0f172a" }}
                        >
                          {scope}
                        </Box>
                        {`: ${
                          SCOPE_DESCRIPTIONS[scope] || "use this permission"
                        }`}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography
                  sx={{ fontSize: "0.9rem", color: "text.secondary" }}
                >
                  No identity information was requested.
                </Typography>
              )}
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
              Approving allows this application to act on your behalf within
              your existing EFabric permissions. Continue only if you recognize
              and trust it.
            </Alert>

            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              <Button
                fullWidth
                variant="outlined"
                disabled={Boolean(submitting)}
                onClick={() => handleDecision("deny")}
                sx={{
                  py: 1.15,
                  borderRadius: 2,
                  borderColor: "#94a3b8",
                  color: "#334155",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {submitting === "deny" ? "Denying…" : "Deny"}
              </Button>
              <Button
                fullWidth
                disabled={Boolean(submitting)}
                onClick={() => handleDecision("approve")}
                sx={{ ...primaryBtnSx, mt: 0 }}
              >
                {submitting === "approve" ? "Approving…" : "Approve"}
              </Button>
            </Stack>
          </>
        )}
      </AuthShell>
    </Box>
  );
}

OAuthConsentPage.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

export default OAuthConsentPage;
